import React, { useState, useEffect, useMemo } from "reactn";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import Fuse from "fuse.js";

// components
import Loader from "shared-anpost-components/components/loader/Loader";
import Pagination from "shared-anpost-components/components/form/pagination/Pagination";
import FormGroup from "shared-anpost-components/components/form/form_group/FormGroup";
import InputGroup from "shared-anpost-components/components/form/input_group/InputGroup";
import Label from "shared-anpost-components/components/form/label/Label";
import Select from "shared-anpost-components/components/form/select/Select";
import Input from "shared-anpost-components/components/form/input/Input";
import { FiltersWrapper, Filter } from "../../../_common/form/filters/filters.styled";
import Error from "../../../_common/error/Error";
import OrderHistoryIcon from "../../../../images/order-history.svg";

// hooks
import profileHooks from "../../../../hooks/profileHooks";
import appHook from "../../../../hooks/appHook";
import {
    EditableGridWrapper,
    EditableGridItem,
    EditableGridBtns,
    EditableGridBtn,
    EditableGridInfo,
    EditableGridCont,
    PriceDisplay,
    PagingSelect,
    SearchWrapper,
    SearchIcon
} from "../../../_common/form/editableGrid/EditableGrid.styled";
import IconButton from "../../../_common/button/IconButton";
import { TransactionsButtonsWrapper } from "./TransactionInfo.styled";

// helpers
import { formatPrice } from "../../../../helpers/Helpers";

const TransactionsGrid = props => {
    const { items, infoBtn, price, search, filters, filterActive, loader, history } = props;
    const { handleBlur, handleFocus, focus, values } = appHook();
    const { setGlobalValue } = profileHooks();
    /**
     * Sets current page
     */
    const [currentPage, setCurrentPage] = useState(1);
    /**
     * Sets items per page
     */
    const [itemsPerPage, setItemsPerPage] = useState(5);

    /**
     * Sets searched Items
     */
    const [searchedItems, setSearchedItems] = useState(items);

    /**
     * Show order details
     */
    const [orderDetails, setOrderDetails] = useState(false);

    /**
     * Set Active Filter
     */
    const [activeFilter, setActiveFilter] = useState(filterActive || "all");

    /**
     * Set Filters List
     */
    const [filtersList, setFiltersList] = useState(filters);

    /**
     * Get the number per page
     */
    const itemLength = items.length;
    const numbOfItems = Math.round(itemLength / 5);
    const ordersPerPage = [];
    for (let i = 0; i < numbOfItems; i += 1) {
        ordersPerPage.push({
            id: (i + 1) * 5,
            available: true,
            value: (i + 1) * 5
        });
    }

    /**
     * handle search of grid
     */
    const fuse = new Fuse(items, {
        keys: ["basketReference", "basketDate", "totalAmount"]
    });
    const handleSearch = e => {
        setOrderDetails(false);
        if (e.target.value) {
            const results = fuse.search(e.target.value);
            const filteredResults = results.map(searchResult => searchResult.item);
            setSearchedItems(filteredResults);
        } else {
            setSearchedItems(items);
        }
    };

    /**
     * Order per page change
     */
    const ordersPerPageChange = e => {
        const val = parseInt(e.target.value, 10);

        // check to see if the selected value is a number (5 is the default)
        if (Number.isNaN(val)) {
            setItemsPerPage(5);
        } else {
            setItemsPerPage(val);
        }
    };

    /**
     * Loop through filters, only show filters that exist in items array
     */
    const checkFilterExist = filter => {
        // "all" filter returns always
        let filterExists = filter.id === "all";
        for (let i = 0; i < items.length; i += 1) {
            if (items[i].filter && items[i].filter === filter.id) {
                filterExists = true;
            }
        }
        return filterExists;
    };

    /**
     * Convert date to string
     */
    const convertDateToString = date => {
        const options = { year: "numeric", month: "long", day: "numeric" };
        // regex to update 20-08-2020 to 08/20/2020 date
        const updateDate = date.replace(/(\d{2})-(\d{2})-(\d{4})/, "$2/$1/$3");
        // update 08/20/2020 to 20 August 2020
        return new Date(updateDate).toLocaleDateString([], options);
    };

    /**
     * Get order detail
     */
    const getOrderDetail = reference => {
        setGlobalValue("transactionHistRef", reference);
        history.push("/Transactions/transaction-info");
    };

    useEffect(() => {
        // set the filter to all results
        if (activeFilter === "all") {
            setSearchedItems(items);
        } else {
            // filter by the selected filter
            const filteredItems = items.filter(item => {
                return item.filter && item.filter === activeFilter;
            });
            setSearchedItems(filteredItems);
        }

        // get a list of the filters that exist in the array of items
        if (filters) {
            const filteredFilterList = filters.filter(item => {
                return checkFilterExist(item);
            });
            setFiltersList(filteredFilterList);
        }
    }, [itemsPerPage, activeFilter, checkFilterExist]);

    const currentPageItems = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * itemsPerPage;
        const lastPageIndex = firstPageIndex + itemsPerPage;
        return searchedItems.slice(firstPageIndex, lastPageIndex);
    }, [currentPage, searchedItems]);

    return (
        <>
            {!orderDetails && (
                <EditableGridWrapper>
                    <>
                        {searchedItems.length > 0 && <p className="mt3">Select a transaction to see more information.</p>}
                        {loader && <Loader local />}
                        <EditableGridCont>
                            {filters && (
                                <FiltersWrapper>
                                    {filtersList.map(item => {
                                        return (
                                            <Filter
                                                key={item.id}
                                                onClick={() => {
                                                    setActiveFilter(item.id);
                                                }}
                                                active={activeFilter === item.id}
                                            >
                                                {item.label}
                                            </Filter>
                                        );
                                    })}
                                </FiltersWrapper>
                            )}
                            {!filters && <div></div>}
                            {search && (
                                <SearchWrapper>
                                    <FormGroup focused={focus === "searchGrid"} covert>
                                        <InputGroup>
                                            <Label htmlFor="searchGrid" text="Search" />
                                            <Input id="searchGrid" type="text" name="searchGrid" keyup={handleSearch} focus={handleFocus} blur={handleBlur} covert={focus !== "searchGrid"} />
                                        </InputGroup>
                                    </FormGroup>
                                    <SearchIcon className="icon icon-search"></SearchIcon>
                                </SearchWrapper>
                            )}
                        </EditableGridCont>
                        {currentPageItems.map(item => {
                            return (
                                <EditableGridItem transactions key={item.basketReference}>
                                    <EditableGridInfo transactions>
                                        {item.basketDate && <h5>{convertDateToString(item.basketDate)}</h5>}
                                        {item.basketReference && <p>Order reference: {item.basketReference}</p>}
                                    </EditableGridInfo>
                                    <EditableGridBtns transactions>
                                        {infoBtn && (
                                            <EditableGridBtn
                                                onClick={() => {
                                                    getOrderDetail(item.basketReference);
                                                }}
                                                aria-label={`Order details for basket with reference of ${item.basketReference} and a price of ${formatPrice(
                                                    item.totalAmount
                                                )}. Click this button to see more information.`}
                                            >
                                                <p>Order Details</p>
                                                <img className="icon order-history" src={OrderHistoryIcon} alt="" />
                                            </EditableGridBtn>
                                        )}
                                        {price && (
                                            <PriceDisplay>
                                                <h5>€{formatPrice(item.totalAmount)}</h5>
                                            </PriceDisplay>
                                        )}
                                    </EditableGridBtns>
                                </EditableGridItem>
                            );
                        })}
                        {!values.loggedIn && <Error errorHeading="You are not logged in. You need to be logged into your An Post account to view this page." loginPrompt></Error>}
                        {searchedItems.length === 0 && values.loggedIn && !loader && (
                            <>
                                <h5 className="mt3">You have no transaction history on this account</h5>
                                <TransactionsButtonsWrapper>
                                    <IconButton label="Click &amp; post" iconCls="icon-basket-label" href="/Post-Parcels/Sending/Calculate-Postage" />
                                    <IconButton label="Shopping Returns" iconCls="icon-basket-reject" href="/Post-Parcels/Click-and-Post/Returns" />
                                </TransactionsButtonsWrapper>
                            </>
                        )}
                        {searchedItems.length > 5 && (
                            <EditableGridCont>
                                <PagingSelect>
                                    <FormGroup focused>
                                        <InputGroup>
                                            <Select
                                                id="ordersperpage"
                                                type="number"
                                                name="ordersperpage"
                                                change={ordersPerPageChange}
                                                defaultValue="Orders per page"
                                                options={ordersPerPage}
                                                required
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                </PagingSelect>
                                <Pagination pageCount={searchedItems.length || itemLength} pageSize={itemsPerPage} pageChange={setCurrentPage} currentPage={currentPage} />
                            </EditableGridCont>
                        )}
                    </>
                </EditableGridWrapper>
            )}
        </>
    );
};

export default withRouter(TransactionsGrid);

TransactionsGrid.propTypes = {
    items: PropTypes.array,
    infoBtn: PropTypes.bool,
    price: PropTypes.bool,
    search: PropTypes.bool,
    filters: PropTypes.array,
    filterActive: PropTypes.string,
    history: PropTypes.object,
    loader: PropTypes.bool
};

